<template lang="pug">
  communications-layout
    custom-number-dialog(
      :value="showSelectCustomNumberDialog"
      @close="showSelectCustomNumberDialog = false"
    )
    sms-compliance-blocked-dialog(
      :value="showSmsComplianceBlockedDialog"
      @close="showSmsComplianceBlockedDialog = false"
    )
    sole-prop-flow-modal
    v-flex.pa-0(xs12)
      v-flex.py-0(xs12)
        v-flex(x12)
          a.trailback(@click="handleGoBack")
            v-icon(small) keyboard_arrow_left
            span Back
        ld-gated-content(:feature="LdFeatures.Text2Sell")
          v-flex(xs12 d-flex)
            slide-drawer(
              v-model="showSlideDrawer"
              ref="slide-drawer"
              :confirm-dismiss="confirmDismiss"
            )
              template(slot="title") {{ shortCodeAction }} Short Code
              template(slot="content")
                short-code-editor(
                  v-model="shortCodeParams"
                  ref="shortCodeEditor"
                  :campaigns="campaignsList"
                  :contact-sources="contactSources"
                  @close="closeShortCodeEditor"
                  @saved="saveShortCode"
                  @input="confirmDismiss = true"
                  @content-valid="isShortCodeContentValid = $event"
                  :action="shortCodeAction"
                )
              template(slot="actions")
                v-btn.primary(
                  @click="$refs.shortCodeEditor.save()"
                  :disabled="!isShortCodeContentValid"
                  data-cy="save-short-code"
                ) Save

            action-title-bar(title="Text2Sell")
              template(slot="sub-title")
                div(v-if="user && user.custom_number")
                  p
                    | Your Primary LionDesk Number is {{ user.custom_number }}
                    v-tooltip(bottom)
                      span Share your custom LionDesk phone number and Short Code to instantly connect with your audiences online.
                      v-icon.pl-1(color="primary" slot="activator") info

                  v-btn.primary.ld-gated-feature(
                    v-if="gatedAction === gatedActions.ComplianceViolationBlocked"
                    slot="activator"
                    @click="showSmsComplianceBlockedDialog = true"
                  ) Create Short Code
                  ld-gated-button.primary(
                    v-if="gatedAction === gatedActions.NoAction"
                    slot="activator"
                    @click="showShortCodeEditor({ action: 'Create' })"
                    :feature="LdFeatures.Text2Sell"
                  ) Create Short Code
                  v-btn.primary.ld-gated-feature(
                    v-else-if="gatedAction === gatedActions.SetCustomNumber"
                    slot="activator"
                    @click="showSelectCustomNumberDialog = true"
                  ) Create Short Code
                  v-btn.primary.ld-gated-feature(
                    v-else-if="gatedAction === gatedActions.Register10DLC"
                    slot="activator"
                    @click="onPending10DLCSend()"
                  ) Create Short Code
                p(v-else)
                  v-icon.pr-1(color="red") info
                  | A Primary LionDesk Phone Number is required in order to use this feature.

          v-card.mt-3
            v-layout(row)
              v-flex(v-if="user && user.custom_number")
                v-data-table(
                  data-cy="short-codes-table"
                  :headers="headers"
                  :items="shortCodes"
                  item-key="id"
                  :loading="isLoading"
                  :total-items="total"
                  @update:pagination="sortResults"
                  :class="{ 'in-progress': isLoading }"
                  hide-actions
                )
                  template(slot="items" slot-scope="props")
                    short-code(
                      :short-code="props.item"
                      @deleted="handleDeleteShortCode"
                      @edit="showShortCodeEditor({ action: 'Edit', shortCode: $event })"
                      @deleted:shortURL="handleDeleteShortUrl"
                      @created:shortURL="handleCreateShortUrl"
                    )
                v-layout.pagination
                  v-flex.pages
                    v-pagination(
                      v-if="paginationPageCount(total, pageSize) > 1"
                      v-model="paginationPage"
                      :length="paginationPageCount(total, pageSize)"
                      :total-visible="7"
                    )
                  v-flex.limits
                    pagination-limit.mr-4(
                      :limits="paginationLimits"
                      :current-limit="pageSize"
                      @change:limit="loadWithNewPageLimit($event, 'shortCodesPageSize')"
                    )

      v-snackbar(:value="showError" :auto-height="true" color="error" bottom) {{ error }}

      remove-resource-modal(
        v-model="showRemoveDialog"
        action="delete"
        resource-singular="short code"
        resource-plural="short codes"
        @close="confirmDeleteShortCodes"
      )
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import CommunicationsLayout from '@/Communications/Layout';
import Pagination from '@/Core/mixins/pagination';
import ToggleSlideDrawer from '@/Core/mixins/toggle-slide-drawer';
import GoBackNavigation from '@/Contacts/mixins/goBackNavigation';
import text2sellHeaders from '@/Communications/config/text2sell-headers';
import ActionTitleBar from '@/Core/components/ActionTitleBar';
import ShortCode from '@/Communications/components/ShortCode';
import ShortCodeEditor from '@/Communications/components/ShortCodeEditor';
import RemoveResourceModal from '@/Tasks/components/RemoveResourceModal';
import PaginationLimit from '@/Core/components/PaginationLimit';
import SlideDrawer from '@/Core/components/SlideDrawer';
import { GATED_ACTIONS } from '@/Core/config/gated-actions';
import sendSMSGate from '@/Core/mixins/send-sms-gate';
import SolePropFlowModal from '@/Core/components/SolePropFlowModal';
import { STEPS_SOLE_PROPRIETOR } from '@/a2p/config';

import { LdGatedButton, LdGatedContent, LdFeatures } from 'lion-vue';
import { REGISTRATION_STATES } from '../../Text/config/registrationStatus';
import { soleProprietor } from '../../Text/config/consoleSteps';

import { EventBus } from '@/event-bus';
const solePropCloseAction = 'text2sell';

export default {
  components: {
    CommunicationsLayout,
    ActionTitleBar,
    ShortCode,
    ShortCodeEditor,
    PaginationLimit,
    RemoveResourceModal,
    SlideDrawer,
    LdGatedContent,
    LdGatedButton,
    SolePropFlowModal,
    CustomNumberDialog: () =>
      import(/* webpackChunkName: 'CustomNumberDialog' */ '@/Core/components/CustomNumberDialog'),
    SmsComplianceBlockedDialog: () =>
      import(/* webpackChunkName: 'SmsComplianceBlockedDialog' */ '@/Core/components/SmsComplianceBlockedDialog'),
  },
  mixins: [Pagination, ToggleSlideDrawer, GoBackNavigation, sendSMSGate],
  async created() {
    if (this.user && this.user.custom_number) {
      this.getShortCodes(this.queryParamObject);
      this.findCampaigns({ $limit: 50 });
      this.loadContactSources();
    }
    if(!this.has10dlcRegister || this.isRegistration10dlcIncomplete) {
      await this.checkFor10dlcRegister();
    }
    await this.getLeadsPlusUserInfo();
    EventBus.$on('sole-prop-modal-close', data => {
      if (data === solePropCloseAction && this.canSendSMS) {
        this.showShortCodeEditor({ action: 'Create' });
      }
    });
  },
  data() {
    return {
      LdFeatures,
      showRemoveDialog: false,
      headers: text2sellHeaders,
      // the ID of the selected short code
      selected: 0,
      shortCodeAction: 'Create',
      // params to pass into short code editor
      shortCodeParams: {
        short_code: '',
        callback_url: '',
        automated_response: '',
        contact_source_id: null,
        campaign_id: null,
      },
      sortParams: {
        descending: false,
        sortBy: 'short_code',
      },
      isShortCodeContentValid: false,
      gatedActions: GATED_ACTIONS,
      showSelectCustomNumberDialog: false,
      showSmsComplianceBlockedDialog: false,
    };
  },
  computed: {
    ...mapGetters('campaigns', ['campaignsList']),
    ...mapGetters({
      contactSources: 'contact/sourcesWithDefaultItem',
    }),
    ...mapGetters('conversations', ['isSendButtonDisabled']),
    ...mapGetters('userInformation', ['shouldGoToLeadsPlusFlow']),
    ...mapState({
      user: state => state.userInformation.user,
      pageSize: state => state.preferences.shortCodesPageSize,
      shortCodes: state => state.shortCodes.shortCodes,
      error: state => state.shortCodes.error,
      total: state => state.shortCodes.totalShortCodes,
      isLoading: state => state.shortCodes.isLoading,
      registrationStatus10dlc: state => state.conversations.registrationStatus10dlc,
      registrationType10dlc: state => state.conversations.registrationType10dlc,
      registrationDate10dlc: state => state.conversations.registrationDate10dlc,
      isLeadsPlusUser: state => state.conversations.isLeadsPlusUser,
      has10dlcRegister: state => state.conversations.has10dlcRegister,
      isRegistration10dlcIncomplete: state => state.conversations.isRegistration10dlcIncomplete,
      addRegisterFeeAddonsSuccess: state => state.payment.addRegisterFeeAddonsSuccess,
      registrationModalClose: state => state.registerSoleProprietorA2p.registrationModalClose,
    }),
    showError() {
      return !!this.error;
    },
    queryParamObject() {
      return {
        page: this.paginationPage,
        descending: this.sortParams.descending,
        sortByField: this.sortParams.sortBy || 'short_code',
        limit: this.pageSize,
      };
    },
    isPostLimitDate() {
      const limitDate = new Date(2023, 8, 1);
      if (new Date() > limitDate) {
        return this.registrationStatus10dlc === REGISTRATION_STATES.CAMPAIGN_COMPLETED;
      }
      return new Date() > limitDate;
    },
    registrationDateValid() {
      const targetDate = new Date(Date.UTC(2023, 6, 20)); // July 20, 2023
      targetDate.setUTCHours(0, 0, 0, 0);

      const userCreatedAt = Date.parse(this.user.created_at);
      return new Date(userCreatedAt) < targetDate;
    },
    // simplify after Aug 31
    canSendSMS() {
      if (this.isPostLimitDate) {
        return this.registrationStatus10dlc === REGISTRATION_STATES.CAMPAIGN_COMPLETED;
      }

      return this.registrationDateValid;
    },
  },
  watch: {
    paginationPage(value) {
      this.selected = 0;
      this.getShortCodes({
        ...this.queryParamObject,
        page: value,
      });
    },
    pageSize(value) {
      this.selected = 0;
      this.getShortCodes(this.queryParamObject);
    },
    addRegisterFeeAddonsSuccess: {
      handler(value) {
        if (!value) { return };
        this.setStep(STEPS_SOLE_PROPRIETOR.STATUS);
        this.setShowRegistrationFlowModal({ bool: true, action: this.registrationModalClose });
        this.setAddRegisterFeeAddonsSuccess(false);
      },
    },
  },
  methods: {
    ...mapActions('shortCodes', [
      'getShortCodes',
      'deleteShortCode',
      'createShortCode',
      'editShortCode',
      'createShortenedUrl',
      'deleteShortenedUrl',
    ]),
    ...mapActions('campaigns', ['findCampaigns']),
    ...mapActions('contact', ['loadContactSources']),
    ...mapActions('registerSoleProprietorA2p', [
      'setShowRegistrationFlowModal',
      'setStep'
    ]),
    ...mapActions('conversations', ['checkFor10dlcRegister', 'getLeadsPlusUserInfo']),
    ...mapActions('payment', ['setAddRegisterFeeAddonsSuccess']),
    // update table when sorting is changed
    sortResults(value) {
      this.sortParams = Object.assign({}, value);
      this.getShortCodes(this.queryParamObject);
    },

    /**
     * @param {String} action - should be either'Create' or 'Edit'
     * @param {Object} shortCode - short code object
     */
    showShortCodeEditor({ action, shortCode }) {
      this.confirmDismiss = false;
      this.isShortCodeContentValid = false;
      this.openSlideDrawer();
      // reset fields every time
      this.clearShortCodeParams();
      this.shortCodeAction = action || this.shortCodeAction;
      this.shortCodeParams = shortCode || this.shortCodeParams;
    },
    handleDeleteShortCode($shortCodeId) {
      this.showRemoveDialog = true;
      this.selected = $shortCodeId;
    },
    confirmDeleteShortCodes(isRemovingResource) {
      this.showRemoveDialog = false;
      if (isRemovingResource) {
        this.deleteShortCode({
          shortCodeId: this.selected,
          queryParams: this.queryParamObject,
        });
      }
    },
    clearShortCodeParams() {
      this.shortCodeParams = Object.assign(
        {},
        {
          short_code: '',
          callback_url: null,
          automated_response: '',
          contact_source_id: null,
          campaign_id: null,
        },
      );
    },
    closeShortCodeEditor() {
      this.confirmDismiss = false;
      this.clearShortCodeParams();
      this.closeSlideDrawer();
    },

    async saveShortCode($shortCode) {
      if (this.shortCodeAction === 'Create') {
        await this.createShortCode({
          shortCodeParams: $shortCode,
          queryParams: this.queryParamObject,
        });
      } else {
        await this.editShortCode({
          shortCodeParams: $shortCode,
          queryParams: this.queryParamObject,
        });
      }

      // only close the slider & reset form if no errors
      if (!this.error) {
        this.closeShortCodeEditor();
      }
    },

    // creating & deleting short URLs
    handleCreateShortUrl($shortCodeId) {
      this.createShortenedUrl({
        shortCodeId: $shortCodeId,
        queryParams: this.queryParamObject,
      });
    },
    handleDeleteShortUrl($deleteParams) {
      this.deleteShortenedUrl({
        ...$deleteParams,
        queryParams: this.queryParamObject,
      });
    },
    async onPending10DLCSend() {
      if (!this.registrationStatus10dlc) {
        this.setStep(STEPS_SOLE_PROPRIETOR.DECISION);
        this.setShowRegistrationFlowModal({ bool: true, action: solePropCloseAction });
        return;
      }

      if (this.registrationStatus10dlc === REGISTRATION_STATES.CAMPAIGN_COMPLETED) {
        this.showShortCodeEditor({ action: 'Create' });
        return;
      }

      if (this.shouldGoToLeadsPlusFlow && this.isLeadsPlusUser) {
        this.setStep(STEPS_SOLE_PROPRIETOR.STATUS);
        this.setShowRegistrationFlowModal({ bool: true, action: null });
        return;
      }

      if (
        this.registrationStatus10dlc === REGISTRATION_STATES.PAYMENT_PENDING ||
        this.registrationStatus10dlc === REGISTRATION_STATES.PAYMENT_CAMPAIGN_RETRY_PENDING ||
        this.registrationStatus10dlc === REGISTRATION_STATES.PAYMENT_BRAND_RETRY_PENDING
      ) {
        this.setStep(STEPS_SOLE_PROPRIETOR.PAYMENT);
        this.setShowRegistrationFlowModal({ bool: true, action: solePropCloseAction });
        return;
      }

      if (
        this.canSendSMS && (
          this.registrationStatus10dlc === REGISTRATION_STATES.BRAND_SUBMITTED ||
          this.registrationStatus10dlc === REGISTRATION_STATES.PAYMENT_RECEIVED ||
          this.registrationStatus10dlc === REGISTRATION_STATES.CAMPAIGN_SUBMITTED ||
          this.registrationStatus10dlc === REGISTRATION_STATES.CAMPAIGN_PENDING)
      ) {
        return this.showShortCodeEditor({ action: 'Create' });
      }
      this.setStep(STEPS_SOLE_PROPRIETOR.STATUS);
      this.setShowRegistrationFlowModal({ bool: true, action: solePropCloseAction });
    },
  },
};
</script>
